import createDuck from 'lib/ducks/remoteDuck'
import Cart from 'lib/api/cart'
import cartItemsDuck from './cart-items/duck'
import sameVoucherCode from './voucher-code'

const remote = createDuck({
  namespace: 'account',
  store: 'cart',
  fetchCallback: (options) => Cart.get(options),
})

const BEGIN = 'account/cart/BEGIN_OPERATION'
const FINISH = 'account/cart/FINISH_OPERATION'
const identityActions = [
  'user/auth/SET_USER',
  'user/auth/SET_GUEST_MODE',
  'user/auth/SET_TOKEN',
  'content/internationalization/SET_SHOP',
]
const writes = [
  'AddToCart',
  'UpdateCart',
  'DeleteFromCart',
  'ADD_TO_CART_FROM_WISHLIST',
]
const recoveryTypes = ['AddToCart', 'UpdateCart', 'ADD_TO_CART_FROM_WISHLIST']
// One coordinator per Redux store, never shared between SSR requests.
const activeWrites = new WeakMap()
const isCart = (cart) =>
  Array.isArray(cart?.items) &&
  (cart.items.length === 0 ||
    (cart.summary?.total != null &&
      cart.summary.total !== '' &&
      Number.isFinite(Number(cart.summary.total)))) &&
  cart.ok !== false &&
  !cart.error
const confirmedCode = (cart) =>
  cart?.summary?.applied_voucher?.is_applied === true
    ? cart.summary.applied_voucher.code
    : null
const identityFields = (state) => ({
  guest: state.auth?.isGuestMode,
  customer: state.auth?.user?.id_customer,
  token: state.auth?.token,
  shop: state.internationalization?.shop,
  country: state.internationalization?.country,
})
const identity = (state) => JSON.stringify(identityFields(state))
const identityUpdate = (action) => {
  switch (action.type) {
    case 'user/auth/SET_USER':
      return { guest: false, customer: action.user?.id_customer }
    case 'user/auth/SET_GUEST_MODE':
      return { guest: true }
    case 'user/auth/SET_TOKEN':
      return { token: action.token }
    case 'content/internationalization/SET_SHOP':
      return { shop: action.shop?.shopId, country: action.shop?.shopSlug }
    default:
      return {}
  }
}
const messageFor = (state, key) => {
  const th = state.internationalization?.language?.split('-')[0] === 'th'
  const messages = {
    busy: th
      ? 'กำลังอัปเดตถุงช้อปปิ้ง กรุณารอสักครู่'
      : 'Your bag is updating. Please wait.',
    unconfirmed: th
      ? 'ยังยืนยันยอดรวมไม่ได้ กรุณารีเฟรชถุงช้อปปิ้งก่อนชำระเงิน'
      : 'We couldn’t confirm your total. Refresh your bag before checkout.',
    voucher: th
      ? 'ไม่สามารถยืนยันการใช้คูปองได้ กรุณาตรวจสอบคูปองก่อนชำระเงิน'
      : 'We couldn’t confirm your voucher. Please check it before checkout.',
    voucherRestored: th
      ? 'คูปองเดิมของคุณยังใช้ได้อยู่'
      : 'Your previous voucher is still applied.',
    voucherRestoreFailed: th
      ? 'ไม่สามารถใช้คูปองเดิมได้อีกครั้ง กรุณาตรวจสอบคูปองและยอดรวมก่อนชำระเงิน'
      : 'We couldn’t restore your previous voucher. Please check your voucher and total before checkout.',
    mutation: th
      ? 'อัปเดตถุงช้อปปิ้งไม่สำเร็จ กรุณาตรวจสอบรายการก่อนลองอีกครั้ง'
      : 'We couldn’t complete that update. Check your bag before trying again.',
  }
  return messages[key]
}
const failure = (message, extra = {}) => ({
  ok: false,
  error: { message },
  ...extra,
})
const errorMessage = (value) => {
  const message =
    value?.error?.message || value?.error || value?.message || value
  return typeof message === 'string' ? message : null
}

// The final view must show the requested edit, not merely a successful HTTP response.
const mutationMatches = (cart, type, options) => {
  if (options.productID == null) return true
  const matchesLine = (item, attributeID) =>
    String(item.id_product) === String(options.productID) &&
    (!Array.isArray(item.attributes) ||
      item.attributes.some(
        (attribute) =>
          attribute.selected &&
          String(attribute.id_product_attribute) === String(attributeID),
      ))
  if (type === 'DeleteFromCart') {
    return !cart.items.some((item) =>
      matchesLine(item, options.productAttributeID),
    )
  }
  if (!['AddToCart', 'UpdateCart'].includes(type)) return true
  const targetAttribute =
    type === 'UpdateCart'
      ? options.productToAddAttributeID
      : options.productAttributeID
  const target = cart.items.find((item) => matchesLine(item, targetAttribute))
  const quantityMatches =
    options.quantity == null ||
    Number(target?.quantity) === Number(options.quantity)
  const oldSizeRemains =
    type === 'UpdateCart' &&
    String(options.productToDeleteAttributeID) !== String(targetAttribute) &&
    cart.items.some(
      (item) =>
        Array.isArray(item.attributes) &&
        matchesLine(item, options.productToDeleteAttributeID),
    )
  return !!target && quantityMatches && !oldSizeRemains
}

const cartDuck = {
  ...remote,
  initialState: {
    ...remote.initialState,
    generation: 0,
    intentRevision: 0,
    needsReconciliation: false,
    operation: null,
  },
  reducer: (state, action) => {
    const current = state || cartDuck.initialState
    if (identityActions.includes(action.type)) {
      const update = identityUpdate(action)
      const context = { ...current.context, ...update }
      const changed =
        current.context &&
        Object.keys(update).some(
          (key) =>
            Object.prototype.hasOwnProperty.call(current.context, key) &&
            current.context[key] !== update[key],
        )
      // Layout repeats the same auth/shop actions on ordinary navigation.
      if (!changed) return { ...current, context }
      return {
        ...cartDuck.initialState,
        context,
        generation: (current.generation || 0) + 1,
        needsReconciliation: true,
      }
    }
    if (
      ['account/voucher/REQUEST', 'account/voucher/RECEIVE'].includes(
        action.type,
      )
    ) {
      return { ...current, intentRevision: (current.intentRevision || 0) + 1 }
    }
    if (action.type === BEGIN) {
      return {
        ...current,
        isFetching: true,
        error: null,
        didInvalidate: false,
        generation: action.generation,
        context: action.context,
        operation: action.operation,
        intentRevision:
          (current.intentRevision || 0) +
          (action.operation.kind === 'voucher' ? 1 : 0),
      }
    }
    if (action.type === FINISH) {
      if (action.generation !== current.generation) return current
      return {
        ...current,
        isFetching: false,
        operation: action.operation,
        ...(action.cart && { payload: action.cart, lastUpdated: Date.now() }),
        error: action.error || null,
        needsReconciliation: !!action.unconfirmed,
        didInvalidate: !!action.unconfirmed,
      }
    }
    if (action.type === remote.types.INVALIDATE) {
      // Context refreshes must not destroy the last confirmed voucher before an edit.
      return { ...current, didInvalidate: true }
    }
    if (
      action.type === remote.types.DONE &&
      current.operation?.status === 'pending'
    )
      return current
    if (action.type === remote.types.RECEIVE) {
      return {
        ...remote.reducer(current, action),
        generation: (current.generation || 0) + 1,
        needsReconciliation: false,
        operation: null,
      }
    }
    return remote.reducer(current, action)
  },
  creators: { ...remote.creators },
}

const run =
  (params = {}, voucherChoice = false) =>
  async (dispatch, getState) => {
    const initial = getState()
    const type = params.type || 'GET'
    const mutation = writes.includes(type)
    const writing = mutation || voucherChoice
    const active = activeWrites.get(dispatch)
    if (active) {
      if (writing)
        return failure(messageFor(initial, 'busy'), {
          busy: true,
          status: 'busy',
        })
      // A checkout/credit read waits for the write, then uses its own full context.
      await active.promise
      if (identity(getState()) !== identity(initial))
        return failure('Cart changed.', { stale: true })
      return dispatch(cartDuck.creators.fetch(params))
    }
    if (writing && initial.cart.isFetching) {
      return failure(messageFor(initial, 'busy'), {
        busy: true,
        status: 'busy',
      })
    }
    if (writing && initial.cart.needsReconciliation) {
      if (voucherChoice) {
        const accountBeforeRefresh = identity(initial)
        const refreshed = await dispatch(
          cartDuck.creators.fetch({ type: 'GET', body: params.body }),
        )
        if (
          !isCart(refreshed) ||
          identity(getState()) !== accountBeforeRefresh
        ) {
          return failure(messageFor(initial, 'unconfirmed'), {
            unconfirmed: true,
            status: 'unconfirmed',
          })
        }
        const alreadyApplied = params.voucherCode
          ? sameVoucherCode(confirmedCode(refreshed), params.voucherCode)
          : !refreshed.summary?.applied_voucher?.code
        if (alreadyApplied) return { status: 'confirmed', cart: refreshed }
        return dispatch(cartDuck.creators.applyVoucher(params))
      }
      return failure(messageFor(initial, 'unconfirmed'), {
        unconfirmed: true,
        status: 'unconfirmed',
      })
    }
    const options = {
      ...params,
      body: {
        is_store_credits_applied: initial.cartData?.isStoreCreditUsed,
        ...params.body,
      },
    }
    const generation = (initial.cart.generation || 0) + 1
    const account = identity(initial)
    const previousCart = initial.cart.payload
    const cartID = previousCart?.metadata?.id_cart
    const code = confirmedCode(previousCart)
    const intentRevision = initial.cart.intentRevision || 0
    let kind = mutation ? 'mutation' : 'view'
    if (voucherChoice) kind = 'voucher'
    const operation = {
      kind,
      status: 'pending',
      // A failed refresh must not erase uncertainty about an earlier write.
      requiresReview: !!(
        kind === 'view' &&
        initial.cart.needsReconciliation &&
        (initial.cart.operation?.requiresReview ||
          initial.cart.operation?.kind !== 'view')
      ),
      ...(voucherChoice && { code: params.voucherCode }),
    }
    dispatch({
      type: BEGIN,
      generation,
      operation,
      context: identityFields(initial),
    })
    const current = () =>
      getState().cart.generation === generation &&
      identity(getState()) === account
    const stale = () =>
      failure('Cart changed. Refresh your bag.', {
        stale: true,
        status: 'unconfirmed',
      })
    const finish = (
      cart,
      status,
      message,
      unconfirmed = false,
      error = null,
    ) => {
      if (!current()) return false
      dispatch({
        type: FINISH,
        generation,
        cart,
        unconfirmed,
        error,
        operation: {
          ...operation,
          status,
          requiresReview: unconfirmed && operation.requiresReview,
          ...(message && { message }),
        },
      })
      return true
    }
    const view = () =>
      Cart.get({
        type: 'GET',
        body: options.body,
        ignoreCache: true,
        keepPayload: true,
      })
    let release
    if (writing) {
      const promise = new Promise((resolve) => {
        release = resolve
      })
      activeWrites.set(dispatch, { promise, identity: account })
    }
    try {
      let response
      let voucherRejected = false
      try {
        response = await Cart.get(
          voucherChoice
            ? {
                type: params.voucherCode ? 'USE_VOUCHER' : 'REMOVE_VOUCHER',
                voucherCode: params.voucherCode,
                ignoreCache: true,
              }
            : options,
        )
        // This adapter flattens HTTP errors into strings. Only the observed
        // invalid-code response proves this replacement was rejected; generic
        // server/transport errors must stay read-only after reconciliation.
        voucherRejected =
          voucherChoice &&
          typeof response === 'string' &&
          response.trim() === 'Invalid Voucher Code'
      } catch (error) {
        response = failure(
          errorMessage(error) || messageFor(initial, 'mutation'),
        )
      }
      if (!current()) return stale()
      let cart = response
      let mutationError = null
      if (voucherChoice || (mutation && !isCart(response))) {
        if (mutation)
          mutationError =
            errorMessage(response) || messageFor(initial, 'mutation')
        // An uncertain write is never replayed. Reconcile once using a read only.
        try {
          cart = await view()
        } catch (error) {
          cart = null
        }
        if (!current()) return stale()
      }
      if (!isCart(cart)) {
        const message = messageFor(initial, 'unconfirmed')
        finish(null, 'unconfirmed', message, true, { message })
        return failure(message, { unconfirmed: true, status: 'unconfirmed' })
      }
      if (
        writing &&
        cartID != null &&
        String(cart.metadata?.id_cart) !== String(cartID)
      ) {
        // A new/merged cart invalidates the old selection; never carry it across.
        finish(cart, 'unconfirmed', messageFor(initial, 'unconfirmed'), true)
        return stale()
      }
      if (voucherChoice) {
        const applied = cart.summary?.applied_voucher
        const matches = params.voucherCode
          ? applied?.is_applied === true &&
            sameVoucherCode(applied.code, params.voucherCode)
          : !applied?.code
        const status = matches ? 'confirmed' : 'rejected'
        let message = matches
          ? null
          : cart.summary?.notice?.voucher?.message ||
            errorMessage(response) ||
            messageFor(initial, 'voucher')
        const restorePrevious =
          !matches &&
          voucherRejected &&
          params.voucherCode &&
          code &&
          !sameVoucherCode(params.voucherCode, code) &&
          !confirmedCode(cart) &&
          cartID != null &&
          (getState().cart.intentRevision || 0) === intentRevision + 1
        if (restorePrevious) {
          // A rejected replacement can clear the server's existing voucher.
          // Revalidate the prior choice once; never copy its old price into state.
          const rejectedCart = cart
          try {
            await Cart.get({
              type: 'USE_VOUCHER',
              voucherCode: code,
              ignoreCache: true,
            })
          } catch {
            // Even an uncertain restore must be reconciled, never repeated.
          }
          if (!current()) return stale()
          try {
            cart = await view()
          } catch {
            cart = null
          }
          if (!current()) return stale()
          if (!isCart(cart)) {
            const unavailable = messageFor(initial, 'unconfirmed')
            finish(rejectedCart, 'unconfirmed', unavailable, true, {
              message: unavailable,
            })
            return failure(unavailable, {
              unconfirmed: true,
              status: 'unconfirmed',
            })
          }
          if (
            String(cart.metadata?.id_cart) !== String(cartID) ||
            (getState().cart.intentRevision || 0) !== intentRevision + 1
          ) {
            finish(
              cart,
              'unconfirmed',
              messageFor(initial, 'unconfirmed'),
              true,
            )
            return stale()
          }
          message = `${message} ${messageFor(
            initial,
            sameVoucherCode(confirmedCode(cart), code)
              ? 'voucherRestored'
              : 'voucherRestoreFailed',
          )}`
        }
        finish(cart, status, message)
        dispatch(cartItemsDuck.creators.updateVoucherErrorMessage(message))
        return { status, cart, message }
      }
      if (mutation && !mutationMatches(cart, type, options) && !mutationError) {
        mutationError = messageFor(initial, 'mutation')
      }
      const canRestore =
        recoveryTypes.includes(type) &&
        !mutationError &&
        code &&
        cartID != null &&
        (getState().cart.intentRevision || 0) === intentRevision &&
        !sameVoucherCode(confirmedCode(cart), code)
      if (canRestore) {
        let applied
        try {
          applied = await Cart.get({
            type: 'USE_VOUCHER',
            voucherCode: code,
            ignoreCache: true,
          })
        } catch (error) {
          applied = error
        }
        if (!current()) return stale()
        try {
          cart = await view()
        } catch (error) {
          cart = null
        }
        if (!current()) return stale()
        if (!isCart(cart)) {
          const message = messageFor(initial, 'unconfirmed')
          finish(null, 'unconfirmed', message, true, { message })
          return failure(message, { unconfirmed: true, status: 'unconfirmed' })
        }
        if (String(cart.metadata?.id_cart) !== String(cartID)) {
          finish(cart, 'unconfirmed', messageFor(initial, 'unconfirmed'), true)
          return stale()
        }
        dispatch(
          cartItemsDuck.creators.updateVoucherErrorMessage(
            sameVoucherCode(confirmedCode(cart), code)
              ? null
              : cart.summary?.notice?.voucher?.message ||
                  errorMessage(applied) ||
                  messageFor(initial, 'voucher'),
          ),
        )
      }
      if (mutation && !mutationMatches(cart, type, options) && !mutationError) {
        mutationError = messageFor(initial, 'mutation')
      }
      finish(cart, mutationError ? 'rejected' : 'confirmed', mutationError)
      return mutationError
        ? failure(mutationError, {
            cart,
            error: {
              ...(typeof response?.error === 'object' && response.error),
              message: mutationError,
            },
          })
        : cart
    } finally {
      if (writing) {
        activeWrites.delete(dispatch)
        release()
      }
      // Account changes can occur between requests, even without a cart action.
      if (!current() && getState().cart.generation === generation) {
        dispatch({
          type: FINISH,
          generation,
          unconfirmed: true,
          operation: { ...operation, status: 'unconfirmed' },
        })
      }
    }
  }

cartDuck.creators.fetch = (params) => run(params)
cartDuck.creators.applyVoucher = (params) => run(params, true)
cartDuck.creators.get =
  (params = {}) =>
  (dispatch, getState) => {
    const state = getState().cart
    if (
      writes.includes(params.type) ||
      params.ignoreCache ||
      state.didInvalidate ||
      state.needsReconciliation
    ) {
      return dispatch(cartDuck.creators.fetch(params))
    }
    return Promise.resolve(state.payload)
  }

export default cartDuck
